import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TopPerformers = ({ data }) => {
  const [view, setView] = useState('roi'); // 'roi' or 'value'

  const sortedByROI = [...(data || [])]?.sort((a, b) => b?.roi - a?.roi)?.slice(0, 10);
  const sortedByValue = [...(data || [])]?.sort((a, b) => b?.current_value - a?.current_value)?.slice(0, 10);

  const displayData = view === 'roi' ? sortedByROI : sortedByValue;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-1">Top Performers</h3>
          <p className="text-sm text-muted-foreground">
            {view === 'roi' ? 'Highest ROI items' : 'Most valuable items'}
          </p>
        </div>
        
        {/* View Toggle */}
        <div className="flex bg-muted/30 rounded-lg p-1">
          <button
            onClick={() => setView('roi')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              view === 'roi' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-card-foreground'
            }`}
          >
            <Icon name="TrendingUp" size={16} />
            <span className="hidden sm:inline">ROI</span>
          </button>
          <button
            onClick={() => setView('value')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              view === 'value' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-card-foreground'
            }`}
          >
            <Icon name="DollarSign" size={16} />
            <span className="hidden sm:inline">Value</span>
          </button>
        </div>
      </div>

      {displayData?.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {displayData?.map((item, index) => (
            <div 
              key={item?.id} 
              className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200"
            >
              {/* Rank Badge */}
              <div className={`flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${
                index === 0 ? 'bg-primary/20 text-primary' :
                index === 1 ? 'bg-warning/20 text-warning' :
                index === 2 ? 'bg-conversion-accent/20 text-conversion-accent': 'bg-muted/50 text-muted-foreground'
              }`}>
                #{index + 1}
              </div>

              {/* Item Image */}
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                <Image
                  src={item?.primary_image_url || '/assets/images/no_image.png'}
                  alt={item?.name || 'Collectible item'}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Item Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-card-foreground truncate">
                  {item?.name}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-muted-foreground capitalize">
                    {item?.collectible_type?.replace('-', ' ')}
                  </span>
                  {item?.condition && (
                    <>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {item?.condition}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Metrics */}
              <div className="text-right">
                <div className="text-sm font-medium text-card-foreground">
                  ${parseFloat(item?.current_value)?.toLocaleString()}
                </div>
                <div className={`text-xs flex items-center justify-end space-x-1 ${
                  item?.roi >= 0 ? 'text-success' : 'text-destructive'
                }`}>
                  <Icon 
                    name={item?.roi >= 0 ? 'ArrowUp' : 'ArrowDown'} 
                    size={12} 
                  />
                  <span>
                    {item?.roi >= 0 ? '+' : ''}{item?.roi?.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon name="Award" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-2">No performers to display</p>
          <p className="text-sm text-muted-foreground">
            Add items with purchase prices to see top performers
          </p>
        </div>
      )}

      {/* Quick Stats */}
      {displayData?.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
          <div className="text-center">
            <div className="text-lg font-bold text-success mb-1">
              +{displayData?.[0]?.roi?.toFixed(2)}%
            </div>
            <div className="text-xs text-muted-foreground">Best ROI</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-primary mb-1">
              ${(() => {
                const avgROI = displayData?.reduce((sum, item) => sum + item?.roi, 0) / displayData?.length;
                return avgROI?.toFixed(2);
              })()}%
            </div>
            <div className="text-xs text-muted-foreground">Avg ROI</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopPerformers;